import React, { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import { CheckCircle, UserCheck, XCircle, BellDot, LogIn, Trash2, ChevronDown } from "lucide-react";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie } from "../utils/getCookie";

const initialLogs = [
  { id: 1, module: "Admin", action: "Logged In", user: "Admin", time: "08:00 AM", date: "13 Oct 2025", icon: LogIn, color: "bg-indigo-100 text-indigo-700" },
  { id: 2, module: "CRM", action: "New Enquiry Added", target: "John Doe", user: "Jane Smith", time: "09:15 AM", date: "12 Aug 2025", icon: UserCheck, color: "bg-blue-100 text-blue-700" },
  { id: 3, module: "Orders", action: "Marked as Out for Delivery", target: "Order #1234", user: "Jane Smith", time: "10:00 AM", date: "12 Nov 2025", icon: CheckCircle, color: "bg-green-100 text-green-700" },
  { id: 4, module: "Invoice", action: "Edited", target: "Invoice #5678", user: "John Doe", time: "11:30 AM", date: "12 Nov 2025", icon: BellDot, color: "bg-yellow-100 text-yellow-700" },
  { id: 5, module: "Task", action: "Marked as Completed", target: "Follow-up Call", user: "Jane Smith", time: "03:45 PM", date: "11 Nov 2025", icon: CheckCircle, color: "bg-green-100 text-green-700" },
  { id: 6, module: "Receipt", action: "Deleted", target: "Invoice #5678", user: "John Doe", time: "05:20 PM", date: "10 Nov 2025", icon: XCircle, color: "bg-red-100 text-red-700" },
  { id: 7, module: "Inventory", action: "Added in Grouped Product", target: "Product ABC", user: "Admin", time: "07:15 PM", date: "09 Nov 2025", icon: UserCheck, color: "bg-blue-100 text-blue-700" },
  { id: 8, module: "Admin", action: "Login Attempt Failed", user: "Admin", time: "07:45 PM", date: "09 Nov 2025", icon: XCircle, color: "bg-red-100 text-red-700" },
];

const dateOptions = [
  { label: "Last 7 Days", value: "7" },
  { label: "Last 30 Days", value: "30" },
  { label: "Last 90 Days", value: "90" },
  { label: "Last 6 Months", value: "180" },
  { label: "Last 1 Year", value: "365" },
  { label: "Custom Dates", value: "custom" },
];

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [selected, setSelected] = useState("365");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const urlmain = process.env.REACT_APP_LINK
  const token = decryptToken("accessToken") || ""
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");

  const fetchData = async () => {
    try {
      const response = await fetch(`${urlmain}/activity`, {
        method: "GET",
        headers: { "Authorization": `Bearer ${token}` }
      });

      const data = await response.json();

      const mapIcon = (heading) => {
        switch (heading) {
          case "Login": return LogIn;
          case "Customer": return UserCheck;
          default: return BellDot;
        }
      };

      const mapColor = (heading) => {
        switch (heading) {
          case "Login": return "bg-indigo-100 text-indigo-700";
          case "Customer": return "bg-blue-100 text-blue-700";
          default: return "bg-gray-100 text-gray-700";
        }
      };

      const formatted = data.map(item => {
        const dateObj = new Date(item.createdAt);

        return {
          id: item.id,
          module: item.heading, // maps your heading
          action: item.description, // action/description
          user: isLoggedIn?.name || "System", // adjust as needed
          time: dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          date: dateObj.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
          }), // matches "13 Oct 2025"
          icon: mapIcon(item.heading),
          color: mapColor(item.heading)
        };
      });

      setLogs(formatted);

    } catch (error) {
      console.error(error);
    }
  };


  console.log(logs)
  const handleDelete = (id) => setLogs((prev) => prev.filter((log) => log.id !== id));

  const parseLogDate = (dateStr) => {
    const [day, month, year] = dateStr.split(" ");
    const monthMap = { Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06", Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12" };
    return new Date(`${year}-${monthMap[month]}-${day.padStart(2, "0")}T00:00:00`);
  };

  const filteredLogs = logs.filter((log) => {
    const logDate = parseLogDate(log.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selected === "custom" && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      return logDate >= start && logDate <= end;
    }

    if (!isNaN(selected)) {
      const days = parseInt(selected, 10);
      const pastDate = new Date(today);
      pastDate.setDate(today.getDate() - days + 1); // inclusive
      pastDate.setHours(0, 0, 0, 0);
      return logDate >= pastDate && logDate <= today;
    }

    return true;
  });

  const groupedLogs = filteredLogs.reduce((acc, log) => {
    if (!acc[log.date]) acc[log.date] = [];
    acc[log.date].push(log);
    return acc;
  }, {});

  useEffect(() => {
    fetchData();
  }, [])

  return (
    <div className="space-y-5 mt-4">
      <h1 className="dashboard-title">Activity Log ({filteredLogs.length} entries)</h1>

      <div className="flex flex-row gap-2 md:items-center mb-4">
        <div className="relative w-fit">
          <select
            value={selected}
            onChange={(e) => {
              const val = e.target.value;
              setSelected(val);
              if (val !== "custom") {
                setStartDate("");
                setEndDate("");
              }
            }}
            className="appearance-none pr-8 pl-3 py-2 rounded-lg border border-gray-300 bg-white text-sm shadow-sm focus:ring-1 focus:ring-blue-500 cursor-pointer outline-0"
          >
            {dateOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        </div>

        {selected === "custom" && (
          <Flatpickr
            options={{ mode: "range", dateFormat: "Y-m-d", conjunction: " to " }}
            value={[startDate, endDate]}
            onChange={([d1, d2]) => {
              setStartDate(d1 ? d1.toISOString().split("T")[0] : "");
              setEndDate(d2 ? d2.toISOString().split("T")[0] : "");
            }}
            className="px-3 py-2 text-sm border rounded-lg shadow-sm bg-white cursor-pointer outline-none w-[220px]"
            placeholder="Select date range"
          />
        )}
      </div>

      <div className="space-y-2">
        {Object.keys(groupedLogs)
          .sort((a, b) => parseLogDate(b) - parseLogDate(a))
          .map((date) => (
            <div key={date} className="bg-white border border-gray-200 rounded-lg py-1 pt-3 px-3">
              <h2 className="text-sm font-semibold text-gray-500 mb-2">{date}</h2>
              <div className="divide-y divide-gray-200">
                {groupedLogs[date]
                  .sort((a, b) => {
                    const t1 = new Date(`${a.date} ${a.time}`);
                    const t2 = new Date(`${b.date} ${b.time}`);
                    return t2 - t1; // newest first
                  })
                  .map((item) => {
                    const Icon = item.icon;
                    const title = item.target
                      ? `${item.module}: ${item.target} ${item.action}`
                      : `${item.module}: ${item.action}`;
                    return (
                      <div key={item.id} className="flex gap-3 items-start py-2">
                        <span className={`p-2 size-10 flex items-center justify-center ${item.color} rounded-lg`}>
                          <Icon size={18} />
                        </span>
                        <div className="flex-1">
                          <p className="text-[11px] text-gray-400 flex items-center gap-1">{item.time}</p>
                          <p className="text-base font-medium text-gray-600">{title}</p>
                        </div>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-500 hover:text-red-700 size-10 rounded-full bg-red-50 flex justify-center items-center hover:bg-red-100"
                          title="Delete log"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ActivityLog;